import { LanguageContext } from "@axon-enterprise/i18n-helpers";
import { AppProvider } from "@axon-enterprise/spark";
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import {
  createContext,
  useState,
  useContext,
  useMemo,
  useCallback,
} from "react";

import { theme as muiTheme } from './index';

// Available themes
export const THEMES = {
  LIGHT: "light",
  DARK: "dark",
};

const ThemeContext = createContext();

export const SparkThemeProvider = ({ children }) => {
  const { language } = useContext(LanguageContext);
  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem("app-theme");
    return savedTheme || THEMES.LIGHT;
  });

  const toggleTheme = useCallback(() => {
    setTheme(prevTheme => {
      const newTheme = prevTheme === THEMES.LIGHT ? THEMES.DARK : THEMES.LIGHT;
      localStorage.setItem("app-theme", newTheme);
      return newTheme;
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      theme,
      toggleTheme,
      isDark: theme === THEMES.DARK,
    }),
    [theme, toggleTheme]
  );

  const appProviderConfig = useMemo(
    () => ({
      theme,
      locale: language,
      direction: "ltr",
      appElement: "#root",
    }),
    [theme, language]
  );

  return (
    <ThemeContext.Provider value={contextValue}>
      <AppProvider {...appProviderConfig}>
        <MuiThemeProvider theme={muiTheme}>
          <CssBaseline />
          {children}
        </MuiThemeProvider>
      </AppProvider>
    </ThemeContext.Provider>
  );
};

SparkThemeProvider.displayName = "SparkThemeProvider";

export const useSparkTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useSparkTheme must be used within a SparkThemeProvider");
  }
  return context;
};
